<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMq } from 'vue3-mq';

// Components
import { BaseCard, BaseEmptyState } from '@magma-app/magma-lapilli';
import BaseUgcDetails from '@/components/bases/BaseUgcDetails.vue';

// Stores
import { useCommonStore } from '@/stores/common';
import { useUgcsStore } from '@/stores/ugcs';
import type { UserDashboardGetAllUgcsResponseType } from '@magma-app/magma-types/all';
const commonStore = useCommonStore();
const ugcsStore = useUgcsStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });
const mq = useMq();

// Reactive variables
const completedUgcDetails = ref<
  UserDashboardGetAllUgcsResponseType['validated'][number] | null
>(null);

// Event Listeners
const onCloseCompletedUgcDetails = () => {
  ugcsStore.setCompletedUgcDetails(false);
  completedUgcDetails.value = null;
};

const onCompletedUgcClick = (
  ugc: UserDashboardGetAllUgcsResponseType['validated'][number]
) => {
  if (!mq.lgPlus) {
    ugcsStore.setCompletedUgcDetails(true);
    completedUgcDetails.value = ugc;
    window.scrollTo(0, 0);
  } else if (mq.lgPlus) {
    commonStore.setManageUgcsModal({
      description: ugc.description,
      open: true,
      item: ugc,
      role: 'ugc-status',
      title: ugc.name,
    });
  }
};

type CompletedUgcStatus =
  UserDashboardGetAllUgcsResponseType['validated'][number]['status'];

// Methods
const badgeStatus = (status: CompletedUgcStatus) => {
  if (status === 'toValidate') {
    return {
      color: 'yellow',
      text: t('generic.validation.pending'),
    };
  } else if (status === 'rejected') {
    return {
      color: 'red',
      text: t('generic.validation.declined'),
    };
  } else {
    return {
      color: 'green',
      text: t('generic.validation.completed'),
    };
  }
};
</script>

<template>
  <div
    class="block px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': ugcsStore.completedUgcs?.length,
      ' px-4 md:px-6': !completedUgcDetails
    }"
  >
    <template v-if="ugcsStore.completedUgcs?.length">
      <template v-if="!completedUgcDetails">
        <BaseCard
          v-for="(ugc, ugcIndex) in ugcsStore.completedUgcs"
          :key="ugcIndex"
          class="mb-2 lg:mb-6 lg:mr-6"
          :title="ugc.name"
          :img-url="
            ugc.pictureUrl
              ? ugc.pictureUrl
              : mq.lgPlus
                ? '/img/ugc_lg.png'
                : '/img/ugc.png'
          "
          :points="ugc.points"
          :display-points="!ugc.pictureUrl"
          :badge-status="badgeStatus(ugc.status)"
          @click="onCompletedUgcClick(ugc)"
        />
      </template>

      <BaseUgcDetails
        v-else
        :ugc="completedUgcDetails"
        :completed="!!completedUgcDetails"
        @close="onCloseCompletedUgcDetails"
      />
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('completedUgcsView.empty.completed')"
      type="empty-list"
    />
  </div>
</template>
